import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

export default function AddressForm({
  addresses = [], // List of existing addresses passed as prop
  onAdd, // Callback invoked with new address
  onUpdate, // Callback invoked with updated address
  onDelete, // Callback invoked with id of address to delete
  clientId,
  defaultAddressType = "hotel", // Used when adding
}) {
  const [listMode, setListMode] = useState(true);
  const [selectedAddress, setSelectedAddress] = useState(null);

  // store type but no dropdown rendering
  const [addressType, setAddressType] = useState(defaultAddressType);

  const [formValues, setFormValues] = useState({
    name: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "",
    phone: "",
    contact_name: "",
    airport_code: "",
    flight_number: "",
    arrival_time: "",
    departure_time: "",
    address_type: defaultAddressType,
  });

  const [errors, setErrors] = useState({});

  const [cities, setCities] = useState([]);
  const [states, setStates] = useState([]);
  const [filterCity, setFilterCity] = useState("");
  const [filterState, setFilterState] = useState("");

  useEffect(() => {
    async function fetchFilters() {
      try {
        const citiesRes = await axios.get("/api/address/cities");
        const statesRes = await axios.get("/api/address/states");
        console.log("Fetched cities:", citiesRes.data);
        console.log("Fetched states:", statesRes.data);
        setCities(citiesRes.data);
        setStates(statesRes.data);
      } catch (error) {
        console.error("Failed to fetch city/state filters", error);
      }
    }
    fetchFilters();
  }, []);

  const filteredAddresses = addresses.filter((addr) => {
    const cityMatch = filterCity
      ? addr.city?.toLowerCase().includes(filterCity.toLowerCase())
      : true;
    const stateMatch = filterState
      ? addr.state?.toLowerCase().includes(filterState.toLowerCase())
      : true;
    return cityMatch && stateMatch;
  });

  // When editing, populate form
  useEffect(() => {
    if (selectedAddress) {
      setAddressType(selectedAddress.address_type);
      setFormValues({ ...selectedAddress });
      setListMode(false);
    } else {
      // Reset form
      setAddressType(defaultAddressType);
      setFormValues({
        name: "",
        address_line1: "",
        address_line2: "",
        city: "",
        state: "",
        postal_code: "",
        country: "",
        phone: "",
        contact_name: "",
        airport_code: "",
        flight_number: "",
        arrival_time: "",
        departure_time: "",
        address_type: defaultAddressType,
      });
      setListMode(true);
    }
  }, [selectedAddress, defaultAddressType]);

  // Validation
  const validate = () => {
    const newErrors = {};
    if (!formValues.address_line1.trim())
      newErrors.address_line1 = "Address Line 1 is required";

    if (addressType === "airport") {
      if (!formValues.airport_code.trim())
        newErrors.airport_code = "Airport Code is required";
      if (!formValues.flight_number.trim())
        newErrors.flight_number = "Flight Number is required";
      if (!formValues.arrival_time)
        newErrors.arrival_time = "Arrival Time is required";
      if (!formValues.departure_time)
        newErrors.departure_time = "Departure Time is required";
    } else if (addressType === "hotel") {
      if (!formValues.name.trim()) newErrors.name = "Hotel Name is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Input change handler
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  // AddressForm.jsx

  const handleSubmit = () => {
    if (!validate()) {
      console.log("Validation failed", errors);
      return;
    }

    const payload = {
      ...formValues,
      address_type: addressType,
      client_id: clientId,
    };

    if (selectedAddress) {
      onUpdate({ ...payload, id: selectedAddress.id }); // no SnackbarAlert here
    } else {
      onAdd(payload); // no SnackbarAlert here
    }

    setSelectedAddress(null);
    setListMode(true);
  };

  // Edit, Delete, Add handlers
  const handleEdit = (address) => {
    setSelectedAddress(address);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure to delete this address ?")) {
      onDelete(id);
    }
  };

  const handleAdd = () => {
    setSelectedAddress(null);
    setListMode(false);
  };

  // Filter addresses based on city and state input

  // List mode
  if (listMode) {
    return (
      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6">Hotel Address</Typography>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={handleAdd}
          >
            Add Address
          </Button>
        </Box>

        {/* Filters */}
       {/* In your filter controls box, add clear button */}
<Box display="flex" gap={2} mb={2} alignItems="center">
  {/* Existing city dropdown */}
  <FormControl sx={{ minWidth: 150 }} size="small">
    <InputLabel id="filter-city-label">City</InputLabel>
    <Select
      labelId="filter-city-label"
      value={filterCity}
      label="City"
      onChange={(e) => setFilterCity(e.target.value)}
      sx={{ height: 32 }}
      MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
    >
      <MenuItem value="">All Cities</MenuItem>
      {cities.map(city => (
        <MenuItem key={city} value={city}>{city}</MenuItem>
      ))}
    </Select>
  </FormControl>

  {/* Existing state dropdown */}
  <FormControl sx={{ minWidth: 150 }} size="small">
    <InputLabel id="filter-state-label">State</InputLabel>
    <Select
      labelId="filter-state-label"
      value={filterState}
      label="State"
      onChange={(e) => setFilterState(e.target.value)}
      sx={{ height: 32 }}
      MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
    >
      <MenuItem value="">All States</MenuItem>
      {states.map(state => (
        <MenuItem key={state} value={state}>{state}</MenuItem>
      ))}
    </Select>
  </FormControl>

  {/* Clear Filters Button */}
  <Button variant="outlined" onClick={() => { setFilterCity(''); setFilterState(''); }} >
    Clear Filters
  </Button>
</Box>
        {filteredAddresses.length === 0 ? (
          <Typography>No addresses found.</Typography>
        ) : (
          <List>
            {filteredAddresses.map((addr) => (
              <React.Fragment key={addr.id}>
                <ListItem
  secondaryAction={
    <>
      <IconButton edge="end" aria-label="edit" onClick={() => handleEdit(addr)} color="primary">
        <EditIcon color="primary" />
      </IconButton>
      <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(addr.id)} color="error">
        <DeleteIcon color="error" />
      </IconButton>
    </>
  }
>

                  <ListItemText
                    primary={`${addr.name || addr.address_type}`}
                    secondary={`${addr.address_line1}, ${addr.city}, ${
                      addr.state
                    } • Phone: ${addr.phone || "N/A"} • Contact: ${
                      addr.contact_name || "N/A"
                    }`}
                  />
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>
    );
  }

  // Form mode
  return (
    <Dialog open onClose={() => setListMode(true)} maxWidth="sm" fullWidth>
      <DialogTitle>{selectedAddress ? "Edit" : "Add"} Address</DialogTitle>
      <DialogContent dividers>
        {/* Common Fields */}
        <TextField
          fullWidth
          margin="normal"
          label="Name"
          name="name"
          value={formValues.name}
          onChange={handleInputChange}
          error={!!errors.name}
          helperText={errors.name}
        />
        <TextField
          fullWidth
          required
          margin="normal"
          label="Address Line 1"
          name="address_line1"
          value={formValues.address_line1}
          onChange={handleInputChange}
          error={!!errors.address_line1}
          helperText={errors.address_line1}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Address Line 2"
          name="address_line2"
          value={formValues.address_line2}
          onChange={handleInputChange}
        />
        <TextField
          fullWidth
          margin="normal"
          label="City"
          name="city"
          value={formValues.city}
          onChange={handleInputChange}
        />
        <TextField
          fullWidth
          margin="normal"
          label="State"
          name="state"
          value={formValues.state}
          onChange={handleInputChange}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Postal Code"
          name="postal_code"
          value={formValues.postal_code}
          onChange={handleInputChange}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Country"
          name="country"
          value={formValues.country}
          onChange={handleInputChange}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Phone"
          name="phone"
          value={formValues.phone}
          onChange={handleInputChange}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Contact Name"
          name="contact_name"
          value={formValues.contact_name}
          onChange={handleInputChange}
        />

        {/* Airport Specific */}
        {addressType === "airport" && (
          <>
            <TextField
              fullWidth
              margin="normal"
              label="Airport Code"
              name="airport_code"
              value={formValues.airport_code}
              onChange={handleInputChange}
              error={!!errors.airport_code}
              helperText={errors.airport_code}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Flight Number"
              name="flight_number"
              value={formValues.flight_number}
              onChange={handleInputChange}
              error={!!errors.flight_number}
              helperText={errors.flight_number}
            />
            <TextField
              fullWidth
              type="datetime-local"
              margin="normal"
              label="Arrival Time"
              name="arrival_time"
              value={formValues.arrival_time}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              error={!!errors.arrival_time}
              helperText={errors.arrival_time}
            />
            <TextField
              fullWidth
              type="datetime-local"
              margin="normal"
              label="Departure Time"
              name="departure_time"
              value={formValues.departure_time}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              error={!!errors.departure_time}
              helperText={errors.departure_time}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setListMode(true)}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          {selectedAddress ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
